/**
 * Role-based access control for the frontend.
 * Defines which roles can perform which actions.
 */
import type { UserRole } from '@/types';

// Action types that can be performed in the application
export type Permission =
  | 'loan.create'
  | 'loan.edit'
  | 'loan.delete'
  | 'loan.transition'
  | 'loan.assign'
  | 'loan.view'
  | 'loan.view_all'
  | 'borrower.create'
  | 'borrower.edit'
  | 'borrower.delete'
  | 'borrower.view'
  | 'comment.create'
  | 'comment.delete'
  | 'document.upload'
  | 'document.delete'
  | 'report.view'
  | 'report.export'
  | 'settings.manage_users'
  | 'settings.manage_system'
  | 'payment.record'
  | 'payment.void'
  | 'ledger.view'
  | 'ledger.create_transaction'
  | 'settings.manage_lenders';

// Permission matrix: which roles have which permissions
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  ADMIN: [
    'loan.create',
    'loan.edit',
    'loan.delete',
    'loan.transition',
    'loan.assign',
    'loan.view',
    'loan.view_all',
    'borrower.create',
    'borrower.edit',
    'borrower.delete',
    'borrower.view',
    'comment.create',
    'comment.delete',
    'document.upload',
    'document.delete',
    'report.view',
    'report.export',
    'settings.manage_users',
    'settings.manage_system',
    'settings.manage_lenders',
    'payment.record',
    'payment.void',
    'ledger.view',
    'ledger.create_transaction',
  ],
  MANAGER: [
    'loan.create',
    'loan.edit',
    'loan.transition',
    'loan.assign',
    'loan.view',
    'loan.view_all',
    'borrower.create',
    'borrower.edit',
    'borrower.view',
    'comment.create',
    'comment.delete',
    'document.upload',
    'document.delete',
    'report.view',
    'report.export',
    'payment.record',
    'payment.void',
    'ledger.view',
    'ledger.create_transaction',
  ],
  OFFICER: [
    'loan.create',
    'loan.edit',
    'loan.transition',
    'loan.view',
    'borrower.create',
    'borrower.edit',
    'borrower.view',
    'comment.create',
    'document.upload',
    'report.view',
    'payment.record',
    'ledger.view',
  ],
  COLLECTOR: [
    'loan.view',
    'loan.transition',
    'borrower.view',
    'comment.create',
    'document.upload',
    'report.view',
    'payment.record',
    'ledger.view',
  ],
  VIEWER: ['loan.view', 'borrower.view', 'report.view', 'ledger.view'],
};

/**
 * Check if a role has a specific permission.
 */
export function hasPermission(role: UserRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Check if a role has any of the specified permissions.
 */
export function hasAnyPermission(role: UserRole | undefined, permissions: Permission[]): boolean {
  if (!role) return false;
  return permissions.some(p => hasPermission(role, p));
}

/**
 * Check if a role has all of the specified permissions.
 */
export function hasAllPermissions(role: UserRole | undefined, permissions: Permission[]): boolean {
  if (!role) return false;
  return permissions.every(p => hasPermission(role, p));
}

/**
 * Get the display label for a role.
 */
export function getRoleLabel(role: UserRole): string {
  const labels: Record<UserRole, string> = {
    ADMIN: 'Administrator',
    MANAGER: 'Manager',
    OFFICER: 'Loans User',
    COLLECTOR: 'Collector',
    VIEWER: 'Viewer',
  };
  return labels[role] || role;
}

/**
 * Get the description for a role.
 */
export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    ADMIN: 'Full system access including user management and system settings',
    MANAGER: 'Can manage loans, borrowers, assignments, and view all reports',
    OFFICER: 'Standard Loans operational access across borrowers, loans, and day-to-day workflows',
    COLLECTOR: 'Can view loans and manage collections workflow',
    VIEWER: 'Read-only access to loans, borrowers, and reports',
  };
  return descriptions[role] || '';
}
